import type { Capability } from "@enduring-systems/entitlements";
import { UNLIMITED } from "@enduring-systems/entitlements";

import type {
  EffectiveAtScope,
  EngineRowState,
  OfferingCatalogEntry,
  ScopeStateCapability,
  ScopeStateView,
  StoredGrantView,
} from "../entitlements-api";

/**
 * Turning the catalog plus one scope's state into what the operator sees.
 *
 * The single rule this module exists to keep: **an offering's state comes from
 * the ENR-1939 scope-state read and from nowhere else.** The catalog supplies
 * presentation (title, price, which capabilities an apply would write) and is
 * never consulted about what is true at a scope, so a card and the raw grants
 * table on the same screen cannot disagree.
 *
 * The second rule follows from the first: the read reports what the ENGINE
 * does with each stored row, and a row the engine would refuse is not in
 * force. An "applied" badge over a denying row would show an operator the
 * opposite of what enforcement does, which is the question this page exists to
 * answer.
 */

/** Why a member of an applied offering is not delivering what it says. */
export type MemberIssue =
  /** The bundle is applied, and this member has no row at the scope at all. */
  | { readonly kind: "missing" }
  /**
   * A stored row the resolution path refuses (ENR-1939).
   *
   * `stillInForce` is the READ's verdict, not a second opinion. An `ignored`
   * row is dropped before the resolver ever sees it, so the other tier at this
   * scope can still be in force, and saying otherwise would contradict the read
   * this module exists to preserve. Only a refusal that leaves nothing in force
   * stops delivery.
   */
  | {
      readonly kind: "engine_refused";
      readonly engineState: Exclude<EngineRowState, "honored">;
      readonly stillInForce: boolean;
    }
  /** Rows exist, none is in force, and the read says why. */
  | { readonly kind: "not_in_force"; readonly reason: string }
  /**
   * What is in force came from something other than this offering's own row.
   *
   * `meetsAuthored` separates an exception that still delivers the offering (an
   * override raising a quota above what the offering grants) from one that
   * neutralizes it (an override holding less). Only the latter stops delivery.
   */
  | {
      readonly kind: "superseded";
      readonly effectiveValue: unknown;
      readonly tier: "override" | "base";
      readonly meetsAuthored: boolean;
    };

/**
 * Whether an issue means the offering is NOT delivering what the card claims.
 *
 * "Worth naming" and "not delivering" are different questions, and collapsing
 * them is how a card comes to badge a healthy scope as broken. A corrupt row
 * beside an honored override is worth naming and IS delivering; a quota an
 * override raised above the offering's own is worth naming and IS delivering.
 */
export function blocksDelivery(issue: MemberIssue): boolean {
  switch (issue.kind) {
    case "missing":
      return true;
    case "engine_refused":
      return !issue.stillInForce;
    case "not_in_force":
      return true;
    case "superseded":
      return !issue.meetsAuthored;
  }
}

export interface MemberView {
  readonly capabilityId: string;
  /**
   * The catalog's authored value, or null for a member only the applied bundle
   * carries. Publication derives the paired boolean from a quota lever, so an
   * applied bundle legitimately holds rows the catalog entry does not list.
   */
  readonly authored: { readonly value: unknown } | null;
  /** Null when nothing about this capability is set at the scope. */
  readonly effective: EffectiveAtScope | null;
  /**
   * The BASE row at this scope, null when there is none.
   *
   * Carried beside `effective` because a quota increase grows this row and not
   * the effective value: `readBaseRows` skips every override, so with an
   * override in force the two differ and a preview built from `effective`
   * would state a from/to the service never writes.
   */
  readonly base: StoredGrantView | null;
  readonly issue: MemberIssue | null;
  /**
   * The capability's registered type, for wording a value the read has no row
   * for. Null when the registry does not define the capability and no row at
   * the scope declares one, which is the only case with no type to appeal to.
   */
  readonly type: string | null;
}

/**
 * One row a retract of this offering would delete at this scope.
 *
 * Derived to match what `retractBundle` actually does rather than what the card
 * describes: the store reads EVERY base row at the scope and deletes the ones
 * whose `source` starts with `bundle:{name}@`. That is version-agnostic, and it
 * is independent of the applied-bundle records, so a preview keyed to the one
 * version the card names would promise a narrower removal than the store
 * performs.
 */
export interface OfferingRemoval {
  readonly capabilityId: string;
  /** The bundle-sourced base row this retract deletes. */
  readonly row: StoredGrantView;
  /**
   * A manually set exception above it, which SURVIVES the retract (ENR-1131
   * deletes on the base sort key only). Null when there is none.
   */
  readonly survivingOverride: StoredGrantView | null;
}

export type OfferingCardState =
  | {
      readonly kind: "applied";
      /** The version this card describes, which is not necessarily the catalog's. */
      readonly appliedVersion: string;
      /**
       * EVERY version of this offering the scope holds, sorted.
       *
       * The server keys applied bundles on `name@version`, so a scope genuinely
       * can hold two at once, which is why the increment path refuses to run
       * against more than one. More than one is a fault in its own right: the
       * members of both are live and no single version describes the scope.
       */
      readonly heldVersions: readonly string[];
      readonly matchesCatalogVersion: boolean;
      /**
       * False when a member issue stops delivery, or when the scope holds more
       * than one version. Never assumed from "applied".
       */
      readonly inForce: boolean;
    }
  /** Not applied, and the registry default already provides every member. */
  | { readonly kind: "plan_default" }
  | { readonly kind: "not_applied" };

export interface OfferingCard {
  readonly offering: OfferingCatalogEntry;
  /** The owning service; null when no member names one. */
  readonly namespace: string | null;
  readonly state: OfferingCardState;
  readonly members: readonly MemberView[];
  /**
   * What a retract would delete here, across every version.
   *
   * Separate from `members` on purpose. `members` answers "is this offering
   * delivering", which is a question about the ONE version the card describes,
   * and folding a version-agnostic set into it would change what `issue` and
   * `inForce` are computed over. This answers only "what would a retract take
   * away", so the two cannot corrupt each other.
   */
  readonly removes: readonly OfferingRemoval[];
}

export interface OfferingSection {
  readonly namespace: string | null;
  readonly offerings: readonly OfferingCard[];
}

export interface BuildSectionsInput {
  readonly offerings: readonly OfferingCatalogEntry[];
  /**
   * Required, not optional. A card state is a claim about a scope, so there is
   * no honest card to draw without the read that answers for it.
   */
  readonly scopeState: ScopeStateView;
  /** The registry, for the namespace-to-service mapping and the defaults. */
  readonly capabilities: readonly Capability[];
}

/**
 * The owning service of a capability id: the registry's `namespace` when it
 * defines the capability, otherwise the id's first dotted segment.
 *
 * The fallback is what lets a service the console has never heard of appear as
 * its own section with no console change (ENR-1960 SRS). It is deliberately
 * not a hand-maintained map: an inventory of "the services we know about" is
 * exactly the thing that falls behind the code.
 */
export function capabilityNamespace(
  capabilityId: string,
  capabilities: readonly Capability[]
): string | null {
  const registered = capabilities.find((capability) => capability.id === capabilityId);
  if (registered) return registered.namespace;
  const [first] = capabilityId.split(".");
  return first === undefined || first === "" ? null : first;
}

/**
 * Values are compared structurally: a quota is a number, a boolean is a
 * boolean, and a config is JSON whose key order the wire preserves from a
 * single serialization, so a stringify comparison holds for the shapes an
 * entitlement value can take.
 */
function sameValue(left: unknown, right: unknown): boolean {
  if (left === right) return true;
  if (typeof left !== "object" || typeof right !== "object") return false;
  if (left === null || right === null) return false;
  return JSON.stringify(left) === JSON.stringify(right);
}

/**
 * Whether what is in force delivers AT LEAST what the offering authored.
 *
 * Not equality. A quota an operator raised above the offering's own amount
 * still delivers the offering, and a scope that has been through the ENR-1947
 * increase flow legitimately holds MORE than the authored amount. Equality here
 * is what turned an approved quota increase into a reported fault.
 *
 * Only a quota is ordered. A boolean the offering turns on must be on; anything
 * else has no ordering to appeal to, so it has to match.
 */
function meetsAuthored(
  effective: Extract<EffectiveAtScope, { inForce: true }>,
  authored: unknown
): boolean {
  if (effective.type === "quota") {
    if (effective.value === UNLIMITED) return true;
    if (authored === UNLIMITED) return false;
    return typeof effective.value === "number" && typeof authored === "number"
      ? effective.value >= authored
      : sameValue(effective.value, authored);
  }
  if (effective.type === "boolean" && authored === true) return effective.value === true;
  return sameValue(effective.value, authored);
}

/** A stored row the resolution path will not honor, with its state narrowed. */
type RefusedRow = StoredGrantView & {
  readonly engineState: Exclude<EngineRowState, "honored">;
};

/** The row the read says is in force, or null when nothing is. */
function inForceRow(entry: ScopeStateCapability): StoredGrantView | null {
  if (!entry.effective.inForce) return null;
  return entry.effective.tier === "override" ? entry.override : entry.base;
}

/**
 * What, if anything, is wrong with one member of an APPLIED offering.
 *
 * The read is the authority on what is in force, and this function never
 * contradicts it: where the read says a value applies, the worst this can
 * report is that the value came from somewhere other than the offering.
 *
 * `appliedSource` is the provenance an apply of THIS offering at THIS version
 * writes. It is the whole answer to "is the offering the thing delivering
 * this", and it is why a quota increase reads as healthy: the increment path
 * rewrites the same bundle-sourced row at a larger amount, so the provenance
 * still names this offering and nothing else outranks it.
 */
function issueFor(
  entry: ScopeStateCapability | undefined,
  authored: { readonly value: unknown } | null,
  appliedSource: string
): MemberIssue | null {
  if (!entry) return { kind: "missing" };

  const refused = [entry.override, entry.base].find(
    (row): row is RefusedRow => row !== null && row.engineState !== "honored"
  );

  if (!entry.effective.inForce) {
    /* Nothing is in force. Name the refused row when there is one, because it
       is the thing a human has to repair; otherwise the read's own reason. */
    return refused
      ? { kind: "engine_refused", engineState: refused.engineState, stillInForce: false }
      : { kind: "not_in_force", reason: entry.effective.reason };
  }

  /* Something IS in force. The delivery question is asked FIRST, ahead of any
     refused row, because a neutralizing exception outranks a repair job: an
     ignored bundle row beside an override holding LESS than the offering grants
     is not "covered by another tier", it is the offering turned off by hand,
     which is the case this whole rule exists to surface. Reporting the refusal
     instead would badge it as delivered. The refused row is still visible per
     tier in the raw grants table, so nothing is lost by ranking it second.

     A value from this offering's own row is not measured against the authored
     amount at all: the increment path only grows it, and an older applied
     version legitimately authored a smaller one, which is drift the card
     reports separately rather than a member fault. */
  const fromThisOffering = inForceRow(entry)?.provenance === appliedSource;
  /* A member the catalog does not author (a derived boolean) has no authored
     amount to fall short of, so no shortfall is invented for it. */
  const meets = authored === null ? true : meetsAuthored(entry.effective, authored.value);

  if (!fromThisOffering && !meets) {
    return {
      kind: "superseded",
      effectiveValue: entry.effective.value,
      tier: entry.effective.tier,
      meetsAuthored: false,
    };
  }

  if (refused) {
    return { kind: "engine_refused", engineState: refused.engineState, stillInForce: true };
  }

  if (fromThisOffering) return null;

  return {
    kind: "superseded",
    effectiveValue: entry.effective.value,
    tier: entry.effective.tier,
    meetsAuthored: true,
  };
}

/**
 * The rows a retract of `bundleName` would delete at this scope.
 *
 * Mirrors `retractBundle` in `services/entitlements-admin-api/src/bundle-store.ts`,
 * which is the only definition that matters here: every BASE row whose `source`
 * starts with `bundle:{name}@`. Three consequences the console has to honour,
 * and each one is a way a narrower rule would lie to an operator:
 *
 * - Version-agnostic. A scope can straddle two versions, and both go.
 * - Not filtered by the applied-bundle records. A row an older version left
 *   behind is deleted whether or not any record still lists its capability.
 * - Base tier only. An override survives even when it carries a bundle source,
 *   so it must never appear as something the retract takes away.
 *
 * The trailing `@` is load-bearing: without it, retracting `custom-domain`
 * would claim `custom-domain-plus`'s rows.
 */
const BUNDLE_SOURCE_PREFIX = "bundle:";

/**
 * Every removal at this scope, grouped by the bundle name that owns it, built in
 * ONE pass over the read.
 *
 * Keyed rather than rescanned per offering: the previous form walked the whole
 * capability list once per card, so a catalog of n offerings over m capabilities
 * did n times m work on every render for one unchanging answer.
 *
 * The grouping key is the segment between `bundle:` and the FIRST `@`, which is
 * the same partition the prefix rule made. A bundle name cannot contain `@` (the
 * service validates that on publication), so for every name this can be asked
 * about, "provenance starts with `bundle:{name}@`" and "the first segment equals
 * {name}" select exactly the same rows. `offering-state.test.ts` pins that on the
 * one input where the two rules could have diverged.
 */
function indexRemovals(scopeState: ScopeStateView): Map<string, OfferingRemoval[]> {
  const byBundle = new Map<string, OfferingRemoval[]>();
  for (const entry of scopeState.capabilities) {
    const base = entry.base;
    if (base === null || base.provenance === null) continue;
    if (!base.provenance.startsWith(BUNDLE_SOURCE_PREFIX)) continue;
    const separator = base.provenance.indexOf("@", BUNDLE_SOURCE_PREFIX.length);
    /* No separator means the row is not `bundle:{name}@{version}` at all, so no
       retract of any offering would match it. Skipped rather than filed under a
       truncated key, which is what the prefix rule did with it too. */
    if (separator === -1) continue;
    const name = base.provenance.slice(BUNDLE_SOURCE_PREFIX.length, separator);
    const removal: OfferingRemoval = {
      capabilityId: entry.capabilityId,
      row: base,
      survivingOverride: entry.override,
    };
    const existing = byBundle.get(name);
    if (existing) {
      existing.push(removal);
    } else {
      byBundle.set(name, [removal]);
    }
  }
  return byBundle;
}

/**
 * The registered type of a capability, for wording a value.
 *
 * From the REGISTRY, not from the read's `registryDefault`: the registry answers
 * for a capability the scope holds no row for, which is exactly the case an
 * apply confirmation has to describe.
 */
function capabilityTypeOf(
  capabilityId: string,
  capabilities: readonly Capability[]
): string | null {
  return capabilities.find((capability) => capability.id === capabilityId)?.type.kind ?? null;
}

function registryDefaultOf(
  capabilityId: string,
  capabilities: readonly Capability[]
): { readonly value: unknown } | null {
  const registered = capabilities.find((capability) => capability.id === capabilityId);
  return registered ? { value: registered.defaultValue.value } : null;
}

/**
 * Whether the registry already provides every member, which makes the offering
 * something the workspace has without anyone applying anything.
 *
 * An offering with no members is not "included by default"; it is an offering
 * with nothing to check, so it is excluded explicitly rather than passing the
 * every() vacuously.
 */
function isPlanDefault(
  offering: OfferingCatalogEntry,
  capabilities: readonly Capability[]
): boolean {
  const members = Object.entries(offering.members);
  if (members.length === 0) return false;
  return members.every(([capabilityId, authored]) => {
    const registryDefault = registryDefaultOf(capabilityId, capabilities);
    return registryDefault !== null && sameValue(registryDefault.value, authored);
  });
}

function buildCard(
  offering: OfferingCatalogEntry,
  scopeState: ScopeStateView,
  capabilities: readonly Capability[],
  removalsByBundle: Map<string, OfferingRemoval[]>
): OfferingCard {
  const authoredIds = Object.keys(offering.members);
  const namespaceOf = (capabilityId: string): string | null =>
    capabilityNamespace(capabilityId, capabilities);
  const namespace = authoredIds.map(namespaceOf).find((value) => value !== null) ?? null;

  /* EVERY held version, not the first match by name: the server keys applied
     bundles on `name@version`, so a scope can hold two, and picking one blindly
     would describe v1 while the scope runs v2 and judge the members against the
     wrong rows. The card describes the CATALOG's version when the scope holds
     it, since that is the one an operator is looking at, and otherwise the
     lowest held. */
  const held = scopeState.appliedBundles.filter((bundle) => bundle.name === offering.name);
  const heldVersions = held.map((bundle) => bundle.version).sort();
  const applied =
    held.find((bundle) => bundle.version === offering.version) ??
    held.find((bundle) => bundle.version === heldVersions[0]);

  /* Authored members lead, because those are the levers a human sets, and an
     authored member the bundle never wrote has to appear or its absence is
     silence rather than an issue. Ids the applied bundle carries beyond them
     follow: publication derives the paired boolean, and an operator reading
     the raw tab will see those rows. */
  const memberIds = applied
    ? [...authoredIds, ...applied.capabilityIds.filter((id) => !authoredIds.includes(id))]
    : authoredIds;

  const appliedSource = applied ? `bundle:${offering.name}@${applied.version}` : "";

  const members: MemberView[] = memberIds.map((capabilityId) => {
    const entry = scopeState.capabilities.find(
      (candidate) => candidate.capabilityId === capabilityId
    );
    const authored =
      capabilityId in offering.members ? { value: offering.members[capabilityId] } : null;
    /* The registry first: it is the authority on a capability's type, and it
       answers even where the scope holds no row. A stored row's own type is the
       fallback for a capability the registry does not define, which is the case
       the raw table also surfaces rather than hides. */
    const type =
      capabilityTypeOf(capabilityId, capabilities) ??
      entry?.base?.type ??
      entry?.override?.type ??
      null;

    return {
      capabilityId,
      authored,
      base: entry?.base ?? null,
      effective: entry?.effective ?? null,
      issue: applied ? issueFor(entry, authored, appliedSource) : null,
      type,
    };
  });

  const removes = removalsByBundle.get(offering.name) ?? [];

  const state: OfferingCardState = applied
    ? {
        kind: "applied",
        appliedVersion: applied.version,
        heldVersions,
        matchesCatalogVersion: applied.version === offering.version,
        inForce:
          heldVersions.length === 1 &&
          members.every((member) => member.issue === null || !blocksDelivery(member.issue)),
      }
    : isPlanDefault(offering, capabilities)
      ? { kind: "plan_default" }
      : { kind: "not_applied" };

  return { offering, namespace, state, members, removes };
}

/**
 * Group the catalog into one section per owning service, in catalog order
 * within a section and alphabetical by service across them, with the
 * no-owning-service section last so a malformed definition is visible rather
 * than sorted into the middle of the real ones.
 */
export function buildOfferingSections(input: BuildSectionsInput): readonly OfferingSection[] {
  /* Built once for the whole catalog, not once per card. */
  const removalsByBundle = indexRemovals(input.scopeState);
  const cards = input.offerings.map((offering) =>
    buildCard(offering, input.scopeState, input.capabilities, removalsByBundle)
  );

  const byNamespace = new Map<string | null, OfferingCard[]>();
  for (const card of cards) {
    const existing = byNamespace.get(card.namespace);
    if (existing) {
      existing.push(card);
    } else {
      byNamespace.set(card.namespace, [card]);
    }
  }

  return [...byNamespace.entries()]
    .sort(([left], [right]) => {
      if (left === null) return 1;
      if (right === null) return -1;
      return left.localeCompare(right);
    })
    .map(([namespace, offerings]) => ({ namespace, offerings }));
}
